import * as range from '@attain-apps/range'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import { Expression } from '../../expression'
import * as node from '../../node'
import type { InstanceHelper } from '../helper'
import type { Key } from '../key'
import type { Type } from '../type'

import { Date as DateInstance, DmsDate } from './date'

/**
 * Casts a range endpoint (start or end) to its appropriate type.
 * Handles DMS date instances, native Dates, date strings, and passthrough for
 * non-date values (e.g. numbers in numeric ranges).
 */
function _castEndpoint(value: unknown): NonNullable<unknown> | undefined {
  if (DateInstance.isDate(value)) {
    // NOTE: Cast is safe — the InstanceHelper interface types fromPlain as
    // returning `unknown`, but the concrete Date.HELPER.fromPlain always
    // returns `DmsDate | undefined`.
    return DateInstance.HELPER.fromPlain(value) as DmsDate | undefined
  }
  if (value instanceof Date) {
    return new DmsDate(value)
  }
  if (typeof value === 'string') {
    const date = new DmsDate(value)
    return isNaN(date.valueOf()) ? undefined : date
  }
  return value ?? undefined
}

/**
 * Generates an instance of the {@link range.Range} type
 *
 * @example
 * // Produces an instance based on the provided dates
 * {
 *  "$range": {
 *    "start": { "$date": "1/1/2020" },
 *    "end": { "$date": "today" }
 *  }
 * }
 * // Produces an instance based on the provided dates with inclusive range
 * {
 *  "$range": {
 *    "includesStart": true,
 *    "includesEnd": true,
 *    "start": { "$date": "1/1/2020" },
 *    "end": { "$date": { day: 1, month: 1, year: 2025 } }
 *  }
 * }
 * // Produces an instance of a range that includes everything
 * {
 *  "$range": {}
 * }
 */
export type Range<out T = any> = Readonly<
  Record<
    Key<Type.Range>,
    T extends range.Range<infer U> ? Range.Params<U> : never
  >
>
export namespace Range {
  export const isRange: TypeChecker<Range> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Range>({
      $range: TypeChecker.Factory.intersection(
        Params.isParams,
        TypeChecker.isNotNullish,
      ),
    }),
  ).usingAlias('Instance.Range')
  export const assertIsRange: TypeAsserter<Range> =
    TypeAsserter.Factory.from(isRange)

  export const HELPER: InstanceHelper<Range> = {
    createNode: ({ $range: arg }, context) => {
      const { start, end, includesEnd, includesStart } = arg
      const params: range.Range.constructor.Params<any> = {
        includesEnd,
        includesStart,
      }
      // If 'start' is provided, convert it to a forValue node and extract its
      // value
      if (start) {
        const _node = node.from(start, context)
        node.ForValue.assertIsForValue(_node)
        params.start = _node.value
      }

      // If 'end' is provided, convert it to a forValue node and extract its
      // value
      if (end) {
        const _node = node.from(end, context)
        node.ForValue.assertIsForValue(_node)
        params.end = _node.value
      }

      return new node.ForValue(new DmsRange(params))
    },
    fromPgString: (pgStr: string): DmsRange<DmsDate> | undefined => {
      const parsed = range.Range.fromPostgres(pgStr, str => new DmsDate(str))
      return new DmsRange(parsed)
    },
    fromPlain: ({
      $range: arg,
    }): DmsRange<NonNullable<unknown>> | undefined => {
      if (TypeChecker.isNullish(arg) || !TypeChecker.isObject(arg)) {
        return undefined
      }
      const { start, end, includesEnd, includesStart } = arg

      return new DmsRange({
        end: end !== undefined ? _castEndpoint(end) : undefined,
        includesEnd,
        includesStart,
        start: start !== undefined ? _castEndpoint(start) : undefined,
      })
    },
    runtimeTypeChecker: TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.instance(DmsRange),
    ),
    typeChecker: isRange,
  }

  export type Params<T = any> = range.Range.constructor.Params<
    NonNullable<Expression<T>>
  >
  export namespace Params {
    export const isParams: TypeChecker<Params> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object({
        end: TypeChecker.Factory.intersection(
          Expression.isExpression,
          TypeChecker.isNotNullish,
        ),
        includesEnd: TypeChecker.Factory.optional(TypeChecker.isBoolean),
        includesStart: TypeChecker.Factory.optional(TypeChecker.isBoolean),
        start: TypeChecker.Factory.intersection(
          Expression.isExpression,
          TypeChecker.isNotNullish,
        ),
      }),
    )
  }
}
export class DmsRange<
  in out T extends NonNullable<unknown>,
> extends range.Range<T> {
  public override union(_range: range.Range<T>): DmsRange<T> {
    return new DmsRange(super.union(_range))
  }

  public override intersection(_range: range.Range<T>): DmsRange<T> {
    return new DmsRange(super.intersection(_range))
  }

  public override toJSON(): range.Range.JSON<T> {
    return super.toJSON() satisfies Range
  }
}
